/**
 * The `?` overlay.
 *
 * Reads the same table the handlers read, so the list cannot drift from what
 * the keys actually do. §6.5.
 */

import { useEffect, useRef } from 'react';
import { SHORTCUTS } from '../state/shortcuts';

export function Shortcuts({ onClose }: { onClose: () => void }) {
  const panel = useRef<HTMLDivElement>(null);

  // Focus moves in, Escape closes, and focus is trapped while it is open -
  // the minimum for a dialog that does not strand a keyboard user.
  useEffect(() => {
    panel.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };

    window.addEventListener('keydown', onKey, true);

    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const groups = Array.from(new Set(SHORTCUTS.map((row) => row.group)));

  return (
    <div className="tb-sheet" onClick={onClose} role="presentation">
      <div
        className="tb-sheet__panel"
        role="dialog"
        aria-modal="true"
        aria-label="Keyboard shortcuts"
        tabIndex={-1}
        ref={panel}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="tb-sheet__head">
          <h2 className="tb-sheet__title">Keyboard shortcuts</h2>
          <button type="button" className="tb-btn tb-btn--quiet" onClick={onClose}>
            Close
          </button>
        </div>

        <div className="tb-keys">
          {groups.map((group) => (
            <section key={group}>
              <h3 className="tb-props__heading">{group}</h3>

              {SHORTCUTS.filter((row) => row.group === group).map((row) => (
                <div className="tb-keys__row" key={`${group}-${row.keys}`}>
                  <span className="tb-keys__what">{row.what}</span>
                  <span className="tb-keys__keys">
                    {row.keys.split(' ').map((key) => (
                      <kbd key={key}>{key}</kbd>
                    ))}
                  </span>
                </div>
              ))}
            </section>
          ))}
        </div>
      </div>
    </div>
  );
}
